/**
 * Online estimator for the trainer's internal sim model (SPEC §6.3 A):
 * recursive least squares on y = P_measured / v_phys with regressors φ = [sin γ, 1, v_phys²].
 * θ = [a, b, c]: a = m_tr·g (trainer mass), b = rolling term + offset, c = aero coefficient.
 * The commanded grade is low-pass filtered (trainer lag) before use as a regressor.
 */
import { G } from './constants';

export interface TrainerModel {
  a: number;
  b: number;
  c: number;
}

export interface EstimatorOptions {
  initialMassKg: number;
  crrSent: number;
  cwSent: number;
  /** Forgetting factor per update (4 Hz). */
  lambda: number;
  /** Trainer response time constant used to filter the regressors (s). */
  lagTauSec: number;
}

export const DEFAULT_ESTIMATOR: EstimatorOptions = {
  initialMassKg: 83,
  crrSent: 0.004,
  // cw = 0: with the physical 50×14 gear the flywheel spins fast, so any trainer aero term would dominate the
  // small forces of easy virtual gears and force strongly negative grades (ill-conditioned). All resistance goes
  // into the grade term instead; c is still estimated for whatever speed-dependent loss the trainer has.
  cwSent: 0,
  lambda: 0.998,
  lagTauSec: 1,
};

export class TrainerModelEstimator {
  theta: [number, number, number];
  private P: number[][];
  private sinF: number | null = null;
  private sinCmd: number | null = null;
  updates = 0;
  readonly opts: EstimatorOptions;

  constructor(opts: Partial<EstimatorOptions> = {}) {
    this.opts = { ...DEFAULT_ESTIMATOR, ...opts };
    const a = this.opts.initialMassKg * G;
    this.theta = [a, a * this.opts.crrSent, this.opts.cwSent];
    this.P = [
      [4e4, 0, 0],
      [0, 400, 0],
      [0, 0, 0.01],
    ];
  }

  get model(): TrainerModel {
    return { a: this.theta[0], b: this.theta[1], c: this.theta[2] };
  }

  /** Estimated trainer-internal mass (kg). */
  get massKg(): number {
    return this.theta[0] / G;
  }

  /** Track the commanded grade through the trainer's lag (call every control step, even without an update). */
  trackCommand(gradePct: number, dtSec: number): number {
    const s = Math.sin(Math.atan(gradePct / 100));
    this.sinCmd = s;
    if (this.sinF === null) this.sinF = s;
    else this.sinF += (s - this.sinF) * (1 - Math.exp(-dtSec / this.opts.lagTauSec));
    return this.sinF;
  }

  /**
   * Is the trainer in a state where the linear model holds?
   * - commanded grade is steady relative to the trainer lag, and
   * - grade is above ~0.3 %: below that the trainer force hits its floor (it cannot push), the model is invalid.
   */
  get informative(): boolean {
    if (this.sinF === null || this.sinCmd === null) return false;
    return Math.abs(this.sinCmd - this.sinF) < 0.004 && this.sinF > 0.003;
  }

  /** One RLS step with a measured flywheel force sample. */
  update(forceMeasured: number, vPhys: number): void {
    if (this.sinF === null) return;
    const phi = [this.sinF, 1, vPhys * vPhys];
    const P = this.P;
    const Pphi = [0, 1, 2].map((i) => P[i]![0]! * phi[0]! + P[i]![1]! * phi[1]! + P[i]![2]! * phi[2]!);
    const denom = this.opts.lambda + phi[0]! * Pphi[0]! + phi[1]! * Pphi[1]! + phi[2]! * Pphi[2]!;
    const k = Pphi.map((x) => x / denom);
    const yhat = this.theta[0] * phi[0]! + this.theta[1] * phi[1]! + this.theta[2] * phi[2]!;
    // Huber-style clamp: one bad sample (transient, sprint) must not throw the model off.
    const err = Math.max(-25, Math.min(25, forceMeasured - yhat));
    const next: [number, number, number] = [
      this.theta[0] + k[0]! * err,
      this.theta[1] + k[1]! * err,
      this.theta[2] + k[2]! * err,
    ];
    // Keep the model physically sensible.
    next[0] = Math.min(150 * G, Math.max(40 * G, next[0])); // 40–150 kg
    next[1] = Math.min(20, Math.max(-5, next[1])); // rolling term + offset (N)
    next[2] = Math.min(1, Math.max(0, next[2]));
    this.theta = next;
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++) P[i]![j] = (P[i]![j]! - k[i]! * Pphi[j]!) / this.opts.lambda;
    // Covariance floor/ceiling to avoid wind-up during long periods without excitation.
    for (let i = 0; i < 3; i++) P[i]![i] = Math.min(P[i]![i]!, i === 0 ? 1e5 : i === 1 ? 2e3 : 0.2);
    this.updates++;
  }
}
